import {
	type GeneratedArtifact,
	GeneratedArtifactType,
} from '@kimuno/api-sdk/lib/types/responses/Ai';
import { Box, Card } from '@mui/material';
import React, { useContext } from 'react';

import {
	borderRadius,
	boxShadows,
	customSizes,
	palette,
} from '../../../assets/styles/themes/tokens';
import CircularProgressFixed from '../../../components/utils/CircularProgressFixed';
import {
	type AiAssistantPageSource,
	aiAssistantPageSource,
	amplitudeContentTypes,
	routes,
} from '../../../constants';
import services from '../../../services';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { isGeneratingPodcastArtifact } from '../../../utils/artifacts';
import createNonPropagatingEventHandler from '../../../utils/createNonPropagatingEventHandler';
import useRouter from '../../../utils/useRouter';
import ArtifactCardContent from './ArtifactCardContent';
import ArtifactCardHeader from './ArtifactCardHeader';
import ArtifactCardImageContent from './ArtifactCardImageContent';

interface ArtifactCardProps {
	artifact: GeneratedArtifact;
	// Where the card is rendered — the dashboard carousel passes `dashboard`;
	// the default covers the card's home, the Meine Inhalte page.
	pageSource?: AiAssistantPageSource;
}

const ArtifactCard: React.FC<ArtifactCardProps> = ({
	artifact,
	pageSource = aiAssistantPageSource.myContent,
}) => {
	const { id, title, type, conversationId, createdAt } = artifact;
	const presignedUrl = 'presignedUrl' in artifact ? artifact.presignedUrl : undefined;
	const { router } = useRouter();
	const { preparePodcastNavigation } = useContext(aiAssistantContext);

	const isImage = type === GeneratedArtifactType.IMAGE;
	const isPodcast = type === GeneratedArtifactType.PODCAST;
	const isGeneratingPodcast = isGeneratingPodcastArtifact(artifact);

	const headerCreatedAt = isGeneratingPodcast ? undefined : createdAt;

	// Documents carry their preview text in `content`, podcasts in `transcript`.
	const getTextContent = () => {
		if ('content' in artifact) return artifact.content;
		if ('transcript' in artifact) return artifact.transcript;
		return '';
	};

	const getContentType = () => {
		if (isImage) return amplitudeContentTypes.aiImage;
		if (isPodcast) return amplitudeContentTypes.aiAudio;
		return amplitudeContentTypes.aiDocument;
	};

	const getArtifactQuery = (): Record<string, string> => {
		if (isImage) return { imageId: id };
		if (isPodcast) return { podcastId: id };
		return { edit: 'true', id };
	};

	const navigateToArtifact = () => {
		if (!conversationId) return;

		services.track.ampliTrackAIAssistantPastChatOpenedEvent({
			conversationId,
			pageSource,
			contentType: getContentType(),
		});

		if (isPodcast) preparePodcastNavigation(isGeneratingPodcast, id);
		// page_source rides the push (query for new tab, state for same-tab) so the
		// AiAssistant Opened emission on /ki-tools attributes to this card's page.
		router.push({
			pathname: `/${routes.aiTools.root}/${conversationId}`,
			query: { ...getArtifactQuery(), page_source: pageSource },
			state: { pageSource },
		});
	};

	const handleClick = createNonPropagatingEventHandler(navigateToArtifact);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleClick(event);
		}
	};

	const renderContent = () => {
		if (isGeneratingPodcast) {
			return (
				<Box
					data-testid="artifact-card-loading-content"
					sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
				>
					<CircularProgressFixed size={32} />
				</Box>
			);
		}
		if (isImage) {
			return presignedUrl ? (
				<ArtifactCardImageContent presignedUrl={presignedUrl} title={title} />
			) : null;
		}
		return <ArtifactCardContent content={getTextContent()} />;
	};

	return (
		<Card
			data-testid="artifact-card"
			role="button"
			tabIndex={0} // it should be focusable in sequential keyboard navigation
			aria-label={`artifact-card-${title}`}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			sx={{
				height: customSizes.artifactCard.cardHeight,
				overflow: 'hidden',
				border: `1px solid ${palette.elevation.outlined}`,
				borderRadius: borderRadius.md,
				boxShadow: boxShadows.elevation1,
				cursor: 'pointer',
				transition: 'box-shadow 0.2s ease-in-out',
				'&:hover': {
					boxShadow: boxShadows.elevation6,
				},
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<ArtifactCardHeader title={title} createdAt={headerCreatedAt} type={type} />
			{renderContent()}
		</Card>
	);
};

export default ArtifactCard;
